import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'

import { recurringInvoiceEditorVariants } from '../constants/variants/recurringInvoiceEditorVariants'
import { useGetInvoiceEditorValues } from '../hooks/editorValues/useGetInvoiceEditorValues'
import { useGetRecurringInvoiceEditorValues } from '../hooks/editorValues/useGetRecurringInvoiceEditorValues'
import { PredefinedInvoiceValues } from '../types/predefinedInvoiceValues'
import { RecurringInvoiceForm } from '../types/recurringInvoiceForm/recurringInvoiceForm'
import { useInvoiceEditor } from './invoiceEditorContext'

interface ContextState {
  invoiceValues: InvoiceForm
  isLoading: boolean
  predefinedInvoiceValues: PredefinedInvoiceValues
  recurringInvoiceValues?: RecurringInvoiceForm // provided only for the recurring invoice editor
}

const InvoiceEditorValuesContext = createContext<ContextState | undefined>(undefined)

interface InvoiceEditorValuesProviderProps {
  children?: ReactNode
}

export const InvoiceEditorValuesProvider = ({ children }: InvoiceEditorValuesProviderProps): ReactElement => {
  const { invoiceEditorVariant } = useInvoiceEditor()

  const isRecurringInvoice = recurringInvoiceEditorVariants.includes(invoiceEditorVariant)

  // Recurring invoices reuse the invoice editor, but they're not simply another sales document
  // (like invoices, quotes, etc.) - while their structure it partially compliant with the
  // sales documents, they store and load their values from a different source.
  // That's why each case gets its own hook. This makes a clear distinction and allows not to mix too
  // much complex logic together. Since we cannot call hooks conditionally, both hooks always
  // run and the one that doesn't apply is switched off through 'enabled' - it issues no
  // requests and skips building its values.
  const standardEditorValues = useGetInvoiceEditorValues({ enabled: !isRecurringInvoice })
  const recurringEditorValues = useGetRecurringInvoiceEditorValues({ enabled: isRecurringInvoice })

  const { invoiceValues, isLoading, predefinedInvoiceValues } = isRecurringInvoice
    ? recurringEditorValues
    : standardEditorValues
  const { recurringInvoiceValues } = recurringEditorValues

  const contextValue = useMemo(
    () => ({ invoiceValues, isLoading, predefinedInvoiceValues, recurringInvoiceValues }),
    [invoiceValues, isLoading, predefinedInvoiceValues, recurringInvoiceValues],
  )

  return <InvoiceEditorValuesContext.Provider value={contextValue}>{children}</InvoiceEditorValuesContext.Provider>
}

export const useInvoiceEditorValues = () => {
  const context = useContext(InvoiceEditorValuesContext)

  if (!context) {
    throw new Error('InvoiceEditorValuesProvider is missing in the module!')
  }

  return context
}
